import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { formatDate } from "../../../api/dates";
import { useSiteProgress, useVisitReport, type ReportRow, type SiteProgress, type VisitReport } from "../../../api/routing";
import { Card, Segmented, Stat, StatusChip, type Tone } from "../../../components/ui";
import "../../../styles/routing.css";
import "../../../styles/visits-dashboard.css";
import { ReportControls } from "../ReportControls";
import ReportsTabs from "../../reports/ReportsTabs";
import { queryFromSearch, reportApiUrl, searchFromQuery, type ReportPeriod, type ReportQuery } from "../reportQuery";
import { Columns, CountBars, Legend, StackedBars } from "./charts";
import {
  byCrew,
  byRegion,
  hasReason,
  inBucket,
  milestoneCounts,
  milestones,
  NO_REGION,
  overTime,
  reasonCounts,
  SERIES,
  seriesOf,
  type Series,
} from "./summary";
import { VisitsMap } from "./VisitsMap";

/** The visits dashboard: the visits report's period and filters, as charts the
 * client can read at a glance -- how many visits were planned and done, by day,
 * crew and region, where they were, why the ones not done were not done -- and
 * how far every site has got, from scoping to completed. Pressing a column, a
 * bar or a reason lists those visits underneath. */

/** A month is what a progress meeting looks back over. */
const PERIOD: ReportPeriod = "month";

type Focus = { kind: "day" | "crew" | "region" | "reason"; key: string; label: string };
type ListMode = "not_done" | "all";

const LIST_MODES: readonly { value: ListMode; label: string }[] = [
  { value: "not_done", label: "Not done" },
  { value: "all", label: "All visits" },
];

const SERIES_TONE: Record<Series, Tone> = { done: "ok", not_done: "warn", overdue: "crit", pending: "quiet" };

export default function VisitsDashboard() {
  const [params, setParams] = useSearchParams();
  const query = queryFromSearch(params, PERIOD);
  const url = reportApiUrl(query);
  const report = useVisitReport(url);
  const progress = useSiteProgress(query.region_id);
  const data = report.data;

  function update(change: Partial<ReportQuery>) {
    setParams(searchFromQuery({ ...query, ...change }, PERIOD), { replace: true });
  }

  return (
    <div className="page-bleed routing-page">
      <ReportsTabs />
      <div className="routing-content visits-dash">
        <header className="schedule-head">
          <h1>Visits dashboard</h1>
          {data ? <span className="report-period">{data.period.label}</span> : null}
          <div className="schedule-nav">
            {data ? (
              <a className="btn btn-primary" href={data.urls.dashboard_pdf}>
                Download PDF
              </a>
            ) : null}
            <Link className="btn" to={`/reports/visits?${searchFromQuery(query).toString()}`}>
              Open as a report
            </Link>
          </div>
        </header>
        <ReportControls query={query} report={data} onChange={update} />
        {report.isPending ? <p className="app-loading">Loading the dashboard…</p> : null}
        {report.isError ? <p className="notice bad">The dashboard could not be loaded: {report.error.message}</p> : null}
        {data ? (
          <Body report={data} progress={progress.data} url={url} refreshing={report.isPlaceholderData} query={query} onChange={update} />
        ) : null}
      </div>
    </div>
  );
}

function Body({
  report,
  progress,
  url,
  refreshing,
  query,
  onChange,
}: {
  report: VisitReport;
  progress: SiteProgress | undefined;
  url: string;
  refreshing: boolean;
  query: ReportQuery;
  onChange: (change: Partial<ReportQuery>) => void;
}) {
  const rows = report.rows;
  const total = report.total;
  const [focus, setFocus] = useState<Focus | null>(null);
  const [mode, setMode] = useState<ListMode>("not_done");
  // A new period or filter is a new set of visits; what was picked out of the
  // last one does not carry over.
  useEffect(() => setFocus(null), [url]);

  const time = useMemo(() => overTime(rows, report.period), [rows, report.period]);
  const crews = useMemo(() => byCrew(rows), [rows]);
  const regions = useMemo(() => byRegion(rows), [rows]);
  const reasons = useMemo(() => reasonCounts(rows, report.options.reasons), [rows, report.options.reasons]);

  function pick(next: Focus, listMode: ListMode) {
    if (focus?.kind === next.kind && focus.key === next.key) {
      setFocus(null);
      return;
    }
    setFocus(next);
    setMode(listMode);
  }
  const chosen = (kind: Focus["kind"]) => (focus?.kind === kind ? focus.key : null);
  const toggle = (outcome: string) => () => onChange({ outcome: query.outcome === outcome ? "" : outcome });

  return (
    <div className={refreshing ? "vd-body vd-refreshing" : "vd-body"}>
      <div className="stat-strip" aria-label="Totals">
        <Stat label="Visits planned" value={total.planned} pressed={query.outcome === ""} onClick={() => onChange({ outcome: "" })} />
        <Stat
          label={`Done${total.completion === null ? "" : ` · ${total.completion}%`}`}
          value={total.done}
          good
          tone="var(--ok-ink)"
          pressed={query.outcome === "done"}
          onClick={toggle("done")}
        />
        <Stat label="Not done" value={total.not_done} tone="var(--warning)" pressed={query.outcome === "not_done"} onClick={toggle("not_done")} />
        {/* The report counts a visit past its day as pending too; here the four
            tiles split the planned visits, as the charts do, and add up to them. */}
        <Stat label="Pending, still to come" value={total.pending - total.overdue} tone="var(--ink-soft)" pressed={query.outcome === "pending"} onClick={toggle("pending")} />
        <Stat label="Pending, past their day" value={total.overdue} tone="var(--blocking)" pressed={query.outcome === "overdue"} onClick={toggle("overdue")} />
        <Stat label="With a job card" value={total.sheets} />
      </div>
      <p className="report-filter-text muted">{report.filters.text}. Visits on archived plans are left out, as on the Schedule.</p>

      <div className="vd-grid">
        {rows.length === 0 ? (
          <>
            <p className="empty-state card vd-span-12">
              No visits were planned for {report.period.label} under this filter. Visits are planned on the <Link to="/routing">Planner</Link> and
              assigned on <Link to="/routing/plans">Plans</Link>.
            </p>
            <Card className="vd-span-12" title="Where the sites are">
              <VisitsMap rows={rows} sites={progress?.sites ?? []} stages={progress?.stages ?? []} initialLayer="sites" />
            </Card>
          </>
        ) : (
          <>
            <Card className="vd-span-12" title={time.unit === "day" ? "Visits by day" : "Visits by week"} sub="Press a column to list its visits">
              <Legend />
              <Columns
                buckets={time.buckets}
                selected={chosen("day")}
                onSelect={(bucket) => pick({ kind: "day", key: bucket.key, label: bucket.label }, "all")}
              />
            </Card>
            <Card className="vd-span-6" title="By field operator" sub="Done of planned">
              <Legend />
              <StackedBars
                label="Visits by field operator"
                buckets={crews}
                selected={chosen("crew")}
                onSelect={(bucket) => pick({ kind: "crew", key: bucket.key, label: bucket.label }, "all")}
              />
            </Card>
            <Card className="vd-span-6" title="Why visits were not done" sub={total.not_done > 0 ? "Press a reason to list those visits" : undefined}>
              {reasons.length > 0 ? (
                <CountBars
                  label="Visits not done, by reason"
                  tone="missed"
                  items={reasons}
                  selected={chosen("reason")}
                  onSelect={(item) => pick({ kind: "reason", key: item.key, label: item.label }, "not_done")}
                />
              ) : (
                <p className="muted">No visit in this view was recorded as not done.</p>
              )}
            </Card>
            <Card className="vd-span-8" title="Where the visits were">
              <VisitsMap rows={rows} sites={progress?.sites ?? []} stages={progress?.stages ?? []} />
            </Card>
            <Card className="vd-span-4" title="By region" sub="Done of planned">
              <Legend />
              <StackedBars
                label="Visits by region"
                buckets={regions}
                selected={chosen("region")}
                onSelect={(bucket) => pick({ kind: "region", key: bucket.key, label: bucket.label }, "all")}
              />
            </Card>
          </>
        )}
        <ProgressCard progress={progress} region={report.options.regions.find((r) => String(r.id) === query.region_id)?.name ?? null} />
      </div>

      {rows.length > 0 ? <VisitList rows={rows} focus={focus} mode={mode} onMode={setMode} onClear={() => setFocus(null)} unit={time.unit} /> : null}
    </div>
  );
}

/** How far every site has got, not only this period's: the contract-wide
 * question the visits alone cannot answer. */
function ProgressCard({ progress, region }: { progress: SiteProgress | undefined; region: string | null }) {
  return (
    <Card className="vd-span-12" title="Site progress" sub={`Every site${region ? ` in ${region}` : ""}, whatever the period`}>
      {progress === undefined ? (
        <p className="muted">Loading the sites…</p>
      ) : (
        <div className="vd-progress">
          <CountBars
            label="Sites reaching each step"
            tone="progress"
            max={progress.total}
            items={milestones(progress).map((m) => ({ key: m.key, label: m.label, count: m.count, note: m.key === "sites" ? undefined : `${m.share}%` }))}
          />
          <div className="table-card">
            <table className="data-table vd-region-table" aria-label="Site progress by region">
              <thead>
                <tr>
                  <th>Region</th>
                  <th className="num">Sites</th>
                  <th className="num">Scoped</th>
                  <th className="num">Assessed</th>
                  <th className="num">Work done</th>
                  <th className="num">Completed</th>
                </tr>
              </thead>
              <tbody>
                {progress.by_region.map((group) => {
                  const [, scoped, assessed, workDone, completed] = milestoneCounts(group.counts, group.total, group.scoped);
                  return (
                    <tr key={group.region_id ?? NO_REGION}>
                      <td>{group.region}</td>
                      <td className="num">{group.total}</td>
                      {[scoped, assessed, workDone, completed].map((m) => (
                        <td key={m.key} className="num">
                          {m.count} <span className="muted">{m.share}%</span>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

function matches(row: ReportRow, focus: Focus, unit: "day" | "week"): boolean {
  switch (focus.kind) {
    case "day":
      return inBucket(row, focus.key, unit);
    case "crew":
      return String(row.crew_id) === focus.key;
    case "region":
      return (row.region ?? NO_REGION) === focus.key;
    case "reason":
      return hasReason(row, focus.key);
  }
}

const FOCUS_NAME: Record<Focus["kind"], string> = { day: "Day", crew: "Field operators", region: "Region", reason: "Reason" };

/** The visits behind whatever was pressed: the drill-down. */
function VisitList({
  rows,
  focus,
  mode,
  onMode,
  onClear,
  unit,
}: {
  rows: readonly ReportRow[];
  focus: Focus | null;
  mode: ListMode;
  onMode: (mode: ListMode) => void;
  onClear: () => void;
  unit: "day" | "week";
}) {
  const shown = rows.filter((row) => (mode === "all" || row.outcome === "not_done") && (focus === null || matches(row, focus, unit)));
  return (
    <section className="vd-list" aria-label="Visits">
      <div className="vd-list-head">
        <h2 className="report-subhead">
          {shown.length} {mode === "not_done" ? "not done" : `visit${shown.length === 1 ? "" : "s"}`}
        </h2>
        <Segmented label="List" options={LIST_MODES} value={mode} onChange={onMode} />
        {focus ? (
          <button type="button" className="pill" aria-pressed="true" onClick={onClear} aria-label={`Stop showing only ${focus.label}`}>
            {FOCUS_NAME[focus.kind]}: {focus.label} ×
          </button>
        ) : (
          <span className="muted">Press a column, a bar or a reason above to narrow this list.</span>
        )}
      </div>
      {shown.length === 0 ? (
        <p className="empty-state card">{mode === "not_done" ? "No visit here was recorded as not done." : "No visits here."}</p>
      ) : (
        <div className="table-card">
          <table className="data-table report-visits">
            <thead>
              <tr>
                <th>Date</th>
                <th>Field operators</th>
                <th>Site</th>
                <th>Region</th>
                <th>Outcome</th>
                <th>Reason / note</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const series = seriesOf(row);
                return (
                  <tr key={row.stop_id} className={row.overdue ? "overdue" : undefined}>
                    <td className="nowrap">{formatDate(row.date)}</td>
                    <td>{row.crew}</td>
                    <td>
                      <Link to={`/sites/${row.site_id}`}>{row.site_name}</Link>
                      {row.work_order ? <span className="sub mono">{row.work_order}</span> : null}
                    </td>
                    <td>{row.region ?? "—"}</td>
                    <td>
                      <StatusChip tone={SERIES_TONE[series]}>{SERIES.find((s) => s.key === series)?.label}</StatusChip>
                    </td>
                    <td>
                      {row.reason_label ? <strong>{row.reason_label}</strong> : null}
                      {row.reason_label && row.note ? " — " : null}
                      {row.note ?? (row.reason_label ? null : "—")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
